import TreeList, { Column, DataStructure } from 'devextreme/ui/tree_list';
import { Cell, CellValue, Column as ExcelColumn, Row, Worksheet } from 'exceljs';

const MIN_COLUMN_WIDTH = 5;
const PIXELS_PER_INDENT = 10;
const PIXELS_PER_EXCEL_WIDTH_UNIT = 8;
const CELL_PADDING = 2;

interface TreeListExcelProps {
    component: TreeList;
    worksheet: Worksheet;
    customizeCell?: (options: { treeCol?: any; rowData?: any; excelCell?: any }) => void;
    customizeCol?: (options: { treeCol?: any; excelCol?: any }) => void;
}

type CustomizeCellFn = (args: { treeCol?: any; rowData?: any; excelCell?: any }) => void;

type CustomizeColFn = (args: { treeCol?: any; excelCol?: any }) => void;

class TreeListHelpersCustom {
    private customizeCell?: CustomizeCellFn;

    private customizeCol?: CustomizeColFn;

    private readonly component: TreeList;

    private readonly worksheet: Worksheet;

    private readonly columns: Column[];

    private readonly dateColumns: Column[];

    private readonly lookupColumns: Column[];

    private readonly booleanColumns: Column[];

    private readonly rootValue: any;

    private readonly parentIdExpr: string;

    private readonly keyExpr: string;

    private readonly dataStructure: DataStructure;

    private HeaderRowCount: number = 1;

    constructor(
        component: TreeList,
        worksheet: Worksheet,
        customizeCell?: CustomizeCellFn,
        customizeCol?: CustomizeColFn,
    ) {
        this.component = component;
        this.worksheet = worksheet;
        this.columns = this.component.getVisibleColumns();

        this.customizeCell = customizeCell;
        this.customizeCol = customizeCol;

        this.dateColumns = this.columns.filter(
            (column) => column.dataType === 'date' || column.dataType === 'datetime',
        );
        this.lookupColumns = this.columns.filter((column) => column.lookup !== undefined);

        this.booleanColumns = this.columns.filter((c) => c.dataType === 'boolean');

        this.rootValue = this.component.option('rootValue');
        this.parentIdExpr = this.component.option('parentIdExpr') as string;
        this.keyExpr = (this.component.option('keyExpr') ?? this.component.getDataSource().key()) as string;
        this.dataStructure = this.component.option('dataStructure') as DataStructure;

        // bug: check ExcelJS's GitHub issues #1352 & #2218
        const properties: any = this.worksheet.properties;
        properties.outlineProperties = {
            summaryBelow: false,
            summaryRight: false,
        };
    }

    private _customizeCell(treeCol?: any, rowData?: any, excelCell?: any): void {
        if (typeof this.customizeCell === 'function') {
            this.customizeCell({
                treeCol,
                rowData,
                excelCell,
            });
        }
    }

    private _customizeCol(treeCol?: any, excelCol?: any): void {
        this.customizeCol!({
            treeCol,
            excelCol,
        });
    }

    // Вспомогательный метод для извлечения данных из LoadResult
    private extractDataFromLoadResult(result: any): any[] {
        // LoadResult может быть:
        // 1. Массивом данных
        // 2. Объектом со свойством data (для пагинации)
        // 3. Объектом с другими свойствами
        if (Array.isArray(result)) {
            return result;
        }

        if (result && typeof result === 'object') {
            // Проверяем наличие свойства data
            if (Array.isArray(result.data)) {
                return result.data;
            }

            // Если data не массив, но есть другие свойства, которые могут содержать данные
            const possibleDataProps = ['items', 'results', 'records'];
            for (const prop of possibleDataProps) {
                if (Array.isArray(result[prop])) {
                    return result[prop];
                }
            }
        }

        console.warn('Unable to extract data array from load result:', result);
        return [];
    }

    // Получить все данные без фильтра, затем применить фильтр с сохранением дочерних элементов
    public getData() {
        const filterExpr = this.component.getCombinedFilter(true);

        // Получаем все данные без фильтра
        return this.component
            .getDataSource()
            .store()
            .load()
            .then((result) => {
                const allData = this.extractDataFromLoadResult(result);

                // Применяем фильтр с сохранением иерархии
                const filteredData = this.filterWithHierarchy(allData, filterExpr);
                return this.processData(filteredData);
            });
    }

    private filterWithHierarchy(data: any[], filterExpr: any): any[] {
        if (!filterExpr) return data;

        const filteredIds = new Set<any>();

        // Сначала находим все элементы, которые удовлетворяют фильтру
        const matchingItems = data.filter((item) => this.evaluateFilter(item, filterExpr));

        // Добавляем ID найденных элементов
        matchingItems.forEach((item) => {
            filteredIds.add(item[this.keyExpr]);
            // Добавляем всех родителей этого элемента
            this.addParentChain(data, item, filteredIds);
        });

        // Для каждого найденного элемента добавляем всех его потомков
        matchingItems.forEach((item) => {
            this.addDescendants(data, item[this.keyExpr], filteredIds);
        });

        // Возвращаем только элементы с ID из filteredIds
        return data.filter((item) => filteredIds.has(item[this.keyExpr]));
    }

    // Метод для добавления всех потомков элемента
    private addDescendants(data: any[], parentId: any, filteredIds: Set<any>): void {
        const children = data.filter((item) => item[this.parentIdExpr] === parentId);
        children.forEach((child) => {
            filteredIds.add(child[this.keyExpr]);
            // Рекурсивно добавляем потомков потомков
            this.addDescendants(data, child[this.keyExpr], filteredIds);
        });
    }

    // Метод для добавления всех родителей элемента
    private addParentChain(data: any[], item: any, filteredIds: Set<any>): void {
        let currentParentId = item[this.parentIdExpr];

        while (currentParentId !== this.rootValue && currentParentId != null) {
            filteredIds.add(currentParentId);
            const parent = data.find((p) => p[this.keyExpr] === currentParentId);
            if (!parent) break;
            currentParentId = parent[this.parentIdExpr];
        }
    }

    // Функция для оценки фильтра с поддержкой всех операций DevExtreme
    private evaluateFilter(item: any, filterExpr: any): boolean {
        if (!filterExpr) return true;

        // Обработка массива фильтров (простой фильтр или логические операции)
        if (Array.isArray(filterExpr)) {
            // Если первый элемент - строка, это простой фильтр [field, operator, value]
            if (typeof filterExpr[0] === 'string') {
                const [field, operator, value] = filterExpr;
                return this.evaluateSimpleFilter(item, field, operator, value);
            }

            // Если первый элемент - массив или объект, это сложный фильтр с логическими операциями
            // Формат: [filter1, 'and'/'or', filter2, 'and'/'or', filter3, ...]
            let result = this.evaluateFilter(item, filterExpr[0]);

            for (let i = 1; i < filterExpr.length; i += 2) {
                const logicalOperator = filterExpr[i];
                const nextFilter = filterExpr[i + 1];
                const nextResult = this.evaluateFilter(item, nextFilter);

                if (logicalOperator === 'and') {
                    result = result && nextResult;
                } else if (logicalOperator === 'or') {
                    result = result || nextResult;
                }
            }

            return result;
        }

        // Если это не массив, возвращаем true (неизвестный формат фильтра)
        return true;
    }

    // Оценка простого фильтра с одним условием
    private evaluateSimpleFilter(item: any, field: string, operator: string, value: any): boolean {
        const fieldValue = this.getValueByStringPath(item, field);

        // Обработка null/undefined значений
        if (fieldValue === null || fieldValue === undefined) {
            return operator === '<>' || operator === 'notEqual';
        }

        switch (operator) {
            case '=':
            case 'equal':
                return fieldValue === value;

            case '<>':
            case 'notEqual':
                return fieldValue !== value;

            case '>':
            case 'greaterThan':
                return fieldValue > value;

            case '>=':
            case 'greaterThanOrEqual':
                return fieldValue >= value;

            case '<':
            case 'lessThan':
                return fieldValue < value;

            case '<=':
            case 'lessThanOrEqual':
                return fieldValue <= value;

            case 'contains':
                return fieldValue && fieldValue.toString().toLowerCase().includes(value.toLowerCase());

            case 'notcontains':
            case 'notContains':
                return !fieldValue || !fieldValue.toString().toLowerCase().includes(value.toLowerCase());

            case 'startswith':
            case 'startsWith':
                return fieldValue && fieldValue.toString().toLowerCase().startsWith(value.toLowerCase());

            case 'endswith':
            case 'endsWith':
                return fieldValue && fieldValue.toString().toLowerCase().endsWith(value.toLowerCase());

            case 'between':
                // value должен быть массивом [min, max]
                if (Array.isArray(value) && value.length === 2) {
                    return fieldValue >= value[0] && fieldValue <= value[1];
                }
                return false;

            default:
                console.warn(`Unknown filter operator: ${operator}`);
                return true;
        }
    }

    private processData(data: any) {
        let rows = data;
        if (this.dataStructure === 'plain') rows = this.convertToHierarchical(rows);
        return this.depthDecorator(rows);
    }

    private depthDecorator(data: any, depth = 0) {
        const result: any[] = [];

        data.forEach((node: any) => {
            result.push({
                ...node,
                depth,
                items: this.depthDecorator('items' in node ? node.items : [], depth + 1),
            });
        });

        return result;
    }

    private convertToHierarchical(data: any, id = this.rootValue) {
        const result: any[] = [];
        const roots: any[] = [];

        data.forEach((node: any) => {
            if (node[this.parentIdExpr] === id) roots.push(node);
        });

        roots.forEach((node) => {
            result.push({
                ...node,
                items: this.convertToHierarchical(data, node[this.keyExpr]),
                depth: 0,
            });
        });

        return result;
    }

    private exportRows(rows: any): void {
        rows.forEach((row: any) => {
            this.exportRow(row);

            if (this.hasChildren(row)) {
                this.exportRows(row.items);
            }
        });
    }

    private exportRow(row: any): void {
        this.formatDates(row);
        this.assignLookupText(row);
        this.getRowData(row);
        this.formatBooleans(row);
        const insertedRow: Row = this.worksheet.addRow(row);
        insertedRow.outlineLevel = row.depth;
        this.worksheet.getCell(`A${insertedRow.number}`).alignment = {
            indent: row.depth * 2,
        };
        this.columns.forEach((col) => {
            if (col.dataField) {
                this._customizeCell(col, row, insertedRow.getCell(col.dataField));
            }
        });
    }

    private getRowData(row: any): void {
        this.columns.forEach((column) => {
            if (column.dataField) {
                row[column.dataField] = this.getValueByStringPath(row, column.dataField);
            }
        });
    }

    private getValueByStringPath<T>(obj: T, path: string): any {
        const keys = path.replace(/\[(\w+)\]/g, '.$1').split('.');
        return keys.reduce((acc: any, key: string) => {
            return acc !== undefined && acc !== null ? acc[key] : undefined;
        }, obj);
    }

    private formatDates(row: any): void {
        this.dateColumns.forEach((column) => {
            if (column.dataField !== undefined) {
                row[column.dataField] = new Date(row[column.dataField]);
            }
        });
    }

    private formatBooleans(row: any): void {
        this.booleanColumns.forEach((column) => {
            const df = column.dataField;
            if (!df) return;

            const val = this.getValueByStringPath(row, df);
            if (typeof val === 'boolean') {
                // Перезаписываем значение строкой "Да"/"Нет"
                row[df] = val ? 'Да' : 'Нет';
            }
        });
    }

    private assignLookupText(row: any): void {
        this.lookupColumns.forEach((column) => {
            if (column.dataField && column.lookup?.calculateCellValue) {
                row[column.dataField] = column.lookup.calculateCellValue(row[column.dataField]);
            }
        });
    }

    private generateColumns(): void {
        this.worksheet.columns = this.columns.map(({ caption, dataField }: Column) => ({
            header: caption,
            key: dataField,
        }));

        for (let i = 0; i < 99; i++) {
            if (this.component.getVisibleColumns(i).length == 0) {
                this.HeaderRowCount = i - 1;
                break;
            }
        }

        if (this.HeaderRowCount > 1) {
            const occupied: { [row: number]: Set<number> } = {};
            const headerRows = this.HeaderRowCount;

            // Insert header rows
            for (let hRow = 0; hRow < headerRows - 1; hRow++) {
                this.worksheet.insertRow(hRow + 1, []);
            }

            let currentCol = 1;
            const maxHeaderDepth = headerRows - 1;

            const columnTracker = []; // Keep track of actual column positions

            for (let hRow = 0; hRow <= maxHeaderDepth; hRow++) {
                let colPos = 1;

                const rowCols = this.component.getVisibleColumns(hRow);
                for (let colIdx = 0; colIdx < rowCols.length; colIdx++) {
                    const column: any = rowCols[colIdx];
                    const colSpan = column.colspan ?? 1;
                    const rowSpan = column.rowspan ?? 1;
                    const caption = column.caption ?? '';

                    // Find next available column index for this row
                    while (occupied[hRow]?.has(colPos)) {
                        colPos++;
                    }

                    const startRow = hRow + 1;
                    const endRow = startRow + rowSpan - 1;
                    const startCol = colPos;
                    const endCol = startCol + colSpan - 1;

                    // Check if already merged
                    let canMerge = true;
                    for (let r = startRow; r <= endRow; r++) {
                        if (!occupied[r - 1]) occupied[r - 1] = new Set();
                        for (let c = startCol; c <= endCol; c++) {
                            if (occupied[r - 1].has(c)) {
                                canMerge = false;
                                break;
                            }
                        }
                        if (!canMerge) break;
                    }

                    if (canMerge && (colSpan > 1 || rowSpan > 1)) {
                        this.worksheet.mergeCells(startRow, startCol, endRow, endCol);
                    }

                    // Mark all merged cells as occupied
                    for (let r = startRow; r <= endRow; r++) {
                        if (!occupied[r - 1]) occupied[r - 1] = new Set();
                        for (let c = startCol; c <= endCol; c++) {
                            occupied[r - 1].add(c);
                        }
                    }

                    // Write the caption at the top-left of the merged cell
                    this.worksheet.getCell(startRow, startCol).value = caption;

                    // Move colPos ahead
                    colPos = endCol + 1;
                }
            }
        }
    }

    private hasChildren(row: any): boolean {
        return row.items && row.items.length > 0;
    }

    private autoFitColumnsWidth(): void {
        this.worksheet.columns.forEach((column: Partial<ExcelColumn>) => {
            let maxLength: number = MIN_COLUMN_WIDTH;

            // first column
            if (column.number === 1 && column.eachCell !== undefined) {
                column.eachCell((cell: Cell) => {
                    const indent: number = cell.alignment?.indent
                        ? cell.alignment.indent * (PIXELS_PER_INDENT / PIXELS_PER_EXCEL_WIDTH_UNIT)
                        : 0;

                    let valueLength = this.getValueLength(cell.value);

                    if (indent + valueLength > maxLength) {
                        maxLength = indent + valueLength;
                    }
                });
            }

            // other columns
            if (column.number !== 1) {
                column.values?.forEach((value: CellValue) => {
                    if (value === null || value === undefined) return;
                    let valueLength = this.getValueLength(value);

                    if (valueLength > maxLength) maxLength = valueLength;
                });
            }

            column.width = maxLength + CELL_PADDING;
        });
    }

    private getValueLength(value: CellValue): number {
        let length = 0;

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            length = value.toString().length;
        }

        if (value instanceof Date) {
            length = value.toLocaleDateString().length;
        }

        return length;
    }

    private CustomizeCols(): void {
        if (typeof this.customizeCell === 'function') {
            this.columns.forEach((treeCol) => {
                this._customizeCol(treeCol, this.worksheet.getColumnKey(treeCol.dataField!));
            });
        }
    }

    public export(): Promise<void> {
        this.component.beginCustomLoading('Экспортировать данные');

        return this.getData().then((rows) => {
            this.generateColumns();
            this.exportRows(rows);
            this.autoFitColumnsWidth();
            this.CustomizeCols();
            this.component.endCustomLoading();
        });
    }
}

function exportTreeList({ component, worksheet, customizeCell, customizeCol }: TreeListExcelProps): Promise<void> {
    const helpers = new TreeListHelpersCustom(component, worksheet, customizeCell, customizeCol);
    return helpers.export();
}

export { exportTreeList };
